/**
 * Global `#book` click tracking.
 *
 * Every anchor that points at the booking anchor (`#book`, `/advocacy#book`,
 * `/advocacy/pricing#book`, …) is captured by one delegated document listener,
 * so no CTA can be added later and silently go untracked — including the
 * sticky mobile bar, ConversionBand, and any future inline CTA.
 *
 * Event: `book_click` (dataLayer + GA4 via trackEvent) plus a mirrored
 * `trackCTA("book_click")` so existing CTA dashboards keep working.
 *
 * Payload:
 *   tier            — last retainer tier the visitor selected/interacted with
 *                     ("foundation" | "priority" | "elite" | "unselected")
 *   tier_source     — where that tier selection came from
 *   destination     — raw href of the clicked anchor
 *   destination_path/destination_hash — parsed target
 *   location        — nearest `[data-book-location]` / section id / aria-label
 *   is_sticky       — true when the click came from a sticky mobile bar
 *   viewport        — "mobile" | "desktop" (<768px = mobile)
 *   label           — trimmed anchor text
 *   page_path       — page the click happened on
 */
import { useEffect } from "react";
import { trackCTA, trackEvent } from "@/lib/tracking";

type TierState = { tier: string; source: string };

let selectedTier: TierState = { tier: "unselected", source: "none" };

/** Record the retainer tier a visitor selected so #book clicks are attributed. */
export function setSelectedBookTier(tier: string | null, source = "unknown") {
  selectedTier = tier
    ? { tier: tier.toLowerCase().replace(/\s+/g, "-"), source }
    : { tier: "unselected", source: "none" };
}

export function getSelectedBookTier(): TierState {
  return selectedTier;
}

const isBookHref = (href: string | null) => {
  if (!href) return false;
  const hash = href.split("#")[1];
  return !!hash && hash.toLowerCase().split("?")[0] === "book";
};

const isSticky = (el: HTMLElement) => {
  let node: HTMLElement | null = el;
  while (node && node !== document.body) {
    if (node.dataset?.stickyCta || node.dataset?.bookLocation === "sticky_mobile")
      return true;
    const cls = typeof node.className === "string" ? node.className : "";
    if (/\bfixed\b/.test(cls) && /\bbottom-0\b/.test(cls)) return true;
    node = node.parentElement;
  }
  return false;
};

const resolveLocation = (el: HTMLElement) => {
  const explicit = el.closest<HTMLElement>("[data-book-location]");
  if (explicit?.dataset.bookLocation) return explicit.dataset.bookLocation;
  // Walk up for the nearest meaningful landmark: id, aria-label, or tag.
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.body) {
    if (node.id) return node.id;
    const label = node.getAttribute("aria-label");
    if (label) return label;
    if (/^(SECTION|FOOTER|HEADER|NAV|ASIDE|DIALOG)$/.test(node.tagName))
      return node.tagName.toLowerCase();
    node = node.parentElement;
  }
  return "unknown";
};

/** Attach the delegated listener once, app-wide. */
export function useBookClickTracking() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest<HTMLAnchorElement>("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!isBookHref(href)) return;

      const { tier, source } = getSelectedBookTier();
      const [pathPart, hashPart = "book"] = (href as string).split("#");
      const sticky = isSticky(anchor);

      const payload = {
        tier,
        tier_source: source,
        destination: href as string,
        destination_path: pathPart || window.location.pathname,
        destination_hash: `#${hashPart}`,
        location: sticky ? "sticky_mobile" : resolveLocation(anchor),
        is_sticky: sticky,
        viewport: window.innerWidth < 768 ? "mobile" : "desktop",
        label: (anchor.textContent || "").trim().slice(0, 80),
        page_path: window.location.pathname,
      };

      trackEvent("book_click", payload);
      trackCTA("book_click", payload);
    };

    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);
}
